import { Component, OnInit } from '@angular/core';
import { RequestUniqueLink, ResponseUniqueLink, UserDataModel } from '../../models/userData.model';
import { UniqueLinkService } from '../../services/unique-link.service';
import { CONSTANTS, CONSTANTS_UNIQUE_LINK } from 'src/app/constants/users.constants';
import { PATTERN } from '../../constants/pattern';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TokenServiceService } from "../../services/token-service.service";
import { Transaction } from "../../models";
import { TranslateService } from '@ngx-translate/core';
import Swal from "sweetalert2";
import { ALERT_ICON_CONSTANTS, GENERAL_CONSTANTS } from 'src/app/core/constants/General-constants';

@Component({
  selector: 'app-user-form',
  templateUrl: './user-form.component.html',
  styleUrls: ['./user-form.component.scss'],
})
export class UserFormComponent implements OnInit {

  constructor(
    private readonly tokenServiceService: TokenServiceService,
    private readonly uniqueLinkService: UniqueLinkService,
    private readonly translate: TranslateService
  ) { }

  public loading: boolean;
  transaction: Transaction;
  public descriptionMultiLink: string;
  public userDataModel: UserDataModel;
  public enableButtonContinue = false;
  public activeLang = 'es';
  public seeAmount = false;
  public userData: FormGroup;
  public emailMaxLength: number = 100;
  public descriptionMaxLength: number = 70;
  public amountMaxLength: number = 20;
  public fullNameMaxLength: number = 50;
  merchantExistsFF = false;

  emailFormControl = new FormControl('', [
    Validators.required,
    Validators.email,
    Validators.maxLength(this.emailMaxLength),
  ]);
  descriptionFormControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(this.descriptionMaxLength),
    Validators.pattern(PATTERN.ESPECIAL_CHARACTER),
  ]);
  amountFormControl = new FormControl('', [
    Validators.required,
    Validators.maxLength(this.amountMaxLength),
    Validators.pattern(PATTERN.DECIMAL_WITHOUT_ZERO),
  ]);
  fullNameFormControl = new FormControl('', [
    Validators.maxLength(this.fullNameMaxLength),
    Validators.pattern(PATTERN.CHARACTERS_SPACE_CHARACTERS),
  ]);

  async ngOnInit() {
    this.loading = true;
    this.transaction = this.tokenServiceService.getAndDecodeToken();

    this.translate.setDefaultLang(this.activeLang);

    this.descriptionMultiLink = this.transaction.TITULO;
    this.seeAmount = parseFloat(this.transaction.ORDER_AMOUNT) === 0 || !this.transaction.ORDER_AMOUNT;
    this.userDataModel = new UserDataModel();

    await this.uniqueLinkService.getFeatureFlagValue(this.transaction.MERCHANT_NAME).then((response) => {
      this.merchantExistsFF = response as boolean;
    }).catch(() => this.merchantExistsFF = false).finally(() => this.loading = false);
  }

  /**
   * @description called from the form to start the process Unique Link
   */
  async sendData() {
    this.loading = true;

    await this.requestUniqueLink();
  }

  /**
   * @description sends the request to the lambda and validates its responses
   */
  async requestUniqueLink() {
    try {
      let amount = '0';
      if (this.seeAmount) {
        amount = `${this.userDataModel.amount}`;
      }

      const body: RequestUniqueLink = {
        email: this.userDataModel.email,
        description: this.userDataModel.description,
        merchantId: Number(this.uniqueLinkService.getMerchantIdQueryParam()),
        token: this.uniqueLinkService.getTokenQueryParam(),
        amount: amount,
        name: this.merchantExistsFF ? CONSTANTS.DEFAULT_PAYVALIDA_NAME : this.userDataModel.fullName,
      };

      this.uniqueLinkService.getUniqueLink(body).subscribe({
        next: (response: ResponseUniqueLink) => {
          switch (response.CODE) {
            case CONSTANTS.CODE_OK:
              window.location.href = `${GENERAL_CONSTANTS.HTTPS_PROTOCOL}${response.DATA.url}`;
              break;
            case CONSTANTS_UNIQUE_LINK.CODE_CUSTOM_EXPIRATION_MESSAGE:
              this.showAlert(
                CONSTANTS.TITLE_ATTENTION,
                ALERT_ICON_CONSTANTS.WARNING,
                response.DESC
              );

              break;
            case CONSTANTS_UNIQUE_LINK.ERROR_CONTENT ||
              CONSTANTS_UNIQUE_LINK.CODE_BAD_STRUCT_EMAIL ||
              CONSTANTS_UNIQUE_LINK.CODE_INVALID_EMAIL:
              this.showAlert(
                CONSTANTS.TITLE_ATTENTION,
                ALERT_ICON_CONSTANTS.WARNING,
                CONSTANTS_UNIQUE_LINK.DESC_BAD_MAIL
              );

              break;
            case CONSTANTS_UNIQUE_LINK.CODE_MULTILINK_EXPIRED:
              this.showAlert(
                CONSTANTS.TITLE_ATTENTION,
                ALERT_ICON_CONSTANTS.WARNING,
                CONSTANTS_UNIQUE_LINK.DESC_MULTILINK_EXPIRED
              );
              break;
            case CONSTANTS_UNIQUE_LINK.CODE_DISABLE_MERCHANT:
              this.showAlert(
                CONSTANTS.TITLE_ATTENTION,
                ALERT_ICON_CONSTANTS.WARNING,
                CONSTANTS_UNIQUE_LINK.DESC_DISABLE_MERCHANT
              );

              break;

            case CONSTANTS_UNIQUE_LINK.CODE_INVALID_NAME:
              this.showAlert(
                CONSTANTS.TITLE_ATTENTION,
                ALERT_ICON_CONSTANTS.WARNING,
                CONSTANTS_UNIQUE_LINK.DESC_INVALID_NAME
              );

              break
            default:
              this.showAlert(
                CONSTANTS.ACTION_ERROR,
                ALERT_ICON_CONSTANTS.ERROR,
                CONSTANTS.GENERAL_ALERT
              );
          }
          this.loading = false;
        },
        error: () => {
          this.showAlert(
            CONSTANTS.GENERAL_ALERT,
            ALERT_ICON_CONSTANTS.ERROR,
            CONSTANTS.ACTION_ERROR
          );
          this.loading = false;
        },
      });
    } catch (_) {
      await this.showAlert(
        CONSTANTS.GENERAL_ALERT,
        ALERT_ICON_CONSTANTS.ERROR,
        CONSTANTS.ACTION_ERROR
      );

      this.loading = false;
    }
  }

  /**
   * @description responsible for validating the digits of the amount
   */
  async enableButton() {
    this.enableButtonContinue =
      this.emailFormControl.status === GENERAL_CONSTANTS.VALID_STRING &&
      this.descriptionFormControl.status === GENERAL_CONSTANTS.VALID_STRING &&
      this.fullNameFormControl.status === GENERAL_CONSTANTS.VALID_STRING;

    if (this.seeAmount) {
      this.enableButtonContinue =
        this.enableButtonContinue && this.amountFormControl.status === GENERAL_CONSTANTS.VALID_STRING;
    }
  }

  /**
   * @description create show alert personalized
   */
  async showAlert(title, icon, message) {
    await Swal.fire({
      title: title,
      text: message,
      icon: icon,
      showCloseButton: true,
      confirmButtonText: CONSTANTS.UNDERSTOOD,
      customClass: {
        popup: 'confirm-popup',
        title: 'confirm-title',
        confirmButton: 'confirm-button',
      },
    });
  }
}
